import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { formatTaxNumber } from 'utils/formatters';
import FileAccessGrantService, { FileAccessGrantListFilters } from 'services/file-access-grant.service';
import { useSnackbar } from 'notistack';

type Props = {
  filters: FileAccessGrantListFilters;
  loading: boolean;
  onFilterChange: (filters: FileAccessGrantListFilters) => void;
};

type PersonOption = {
  id: string;
  name: string;
  email: string;
};

type CarrierOption = {
  id: string;
  documentNumber: string;
  tradeName: string;
  companyName?: string;
};

const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.\-/]/g, '');
};

const GrantAccessTableFilters: React.FC<Props> = ({ filters, loading, onFilterChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [localFilters, setLocalFilters] = useState<FileAccessGrantListFilters>(filters);

  const [persons, setPersons] = useState<PersonOption[]>([]);
  const [carriers, setCarriers] = useState<CarrierOption[]>([]);

  const [loadingPersons, setLoadingPersons] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(false);

  const fileAccessGrantService = new FileAccessGrantService();

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    const fetchDistinctEntities = async () => {
      try {
        setLoadingPersons(true);
        setLoadingCarriers(true);

        const data = await fileAccessGrantService.getDistinctEntities();

        const personOptions: PersonOption[] = data.persons.map((p) => ({
          id: p.id.toString(),
          name: p.name,
          email: p.email,
        }));

        const carrierOptions: CarrierOption[] = data.carrierBranches.map((b) => ({
          id: b.id.toString(),
          documentNumber: b.documentNumber,
          tradeName: b.tradeName,
          companyName: b.rootCompanyName || b.companyName || '',
        }));

        setPersons(personOptions);
        setCarriers(carrierOptions);
      } catch (err) {
        enqueueSnackbar('Erro ao buscar filtros. Contate o suporte.', { variant: 'error' });
      } finally {
        setLoadingPersons(false);
        setLoadingCarriers(false);
      }
    };

    fetchDistinctEntities();
  }, []);

  const personFilterOptions = (options: PersonOption[], state: { inputValue: string }) => {
    const inputValue = normalizeString(state.inputValue);
    return options.filter(
      (option) => normalizeString(option.name).includes(inputValue) || normalizeString(option.email).includes(inputValue)
    );
  };

  const carrierFilterOptions = (options: CarrierOption[], state: { inputValue: string }) => {
    const inputValue = normalizeString(state.inputValue);
    return options.filter(
      (option) =>
        normalizeString(option.tradeName || '').includes(inputValue) ||
        normalizeString(option.companyName || '').includes(inputValue) ||
        normalizeString(formatTaxNumber(option.documentNumber)).includes(inputValue)
    );
  };

  const applyPersonFilter = (person: PersonOption | null) => {
    const updated = {
      ...localFilters,
      personEmail: person?.email || '',
      personName: '',
    };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const applyCarrierFilter = (carrier: CarrierOption | null) => {
    const updated = {
      ...localFilters,
      carrierBranchDocumentNumber: carrier ? carrier.documentNumber : '',
      carrierBranchTradeName: '',
    };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Box sx={{ flex: 1 }}>
        <Autocomplete
          options={persons}
          value={persons.find((p) => p.email === localFilters.personEmail) || null}
          onChange={(_, value) => applyPersonFilter(value)}
          filterOptions={personFilterOptions}
          loading={loadingPersons}
          disabled={loading}
          getOptionLabel={(option) => `${option.name} - ${option.email}`}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography fontSize={14} fontWeight={600}>
                  {option.name}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {option.email}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Usuário"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon />,
                endAdornment: (
                  <>
                    {loadingPersons && <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Autocomplete
          options={carriers}
          value={carriers.find((c) => c.documentNumber === localFilters.carrierBranchDocumentNumber) || null}
          onChange={(_, value) => applyCarrierFilter(value)}
          filterOptions={carrierFilterOptions}
          loading={loadingCarriers}
          disabled={loading}
          getOptionLabel={(option) => `${formatTaxNumber(option.documentNumber)} - ${option.companyName || option.tradeName}`}
          isOptionEqualToValue={(option, value) => option.documentNumber === value.documentNumber}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography fontSize={14} fontWeight={600}>
                  {formatTaxNumber(option.documentNumber)}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {option.companyName || option.tradeName}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon />,
                endAdornment: (
                  <>
                    {loadingCarriers && <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default GrantAccessTableFilters;
